// Financial Freedom Calculator -- the interactive wealth-trajectory area chart.
//
// Plots the 50+ vectorized backend data points as a premium gradient-filled
// area. A useMemo scan finds the exact age where `is_retired` first flips true
// so we can snap a ReferenceLine onto the accumulation/decumulation boundary.
// All monetary formatting is currency-aware (the calculator serves a global
// audience) via the shared utils/currency helpers.

import React, { useMemo } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    type TooltipProps,
    XAxis,
    YAxis,
} from 'recharts';

import type { WealthTrajectoryPoint } from '../types';
import { currencySymbol, formatCurrency } from '../utils/currency';

interface TrajectoryChartProps {
    data: WealthTrajectoryPoint[];
    isFetching: boolean;
    currency: string;
}

// Compact Y-axis labels (e.g. €1.5M / $500k) keep the chart uncluttered and
// prevent horizontal squishing. Symbol follows the selected currency.
const makeFormatYAxis = (currency: string) => (tick: number): string => {
    const sym = currencySymbol(currency);
    if (tick === 0) return `${sym}0`;
    if (Math.abs(tick) >= 1_000_000) return `${sym}${(tick / 1_000_000).toFixed(1)}M`;
    if (Math.abs(tick) >= 1_000) return `${sym}${(tick / 1_000).toFixed(0)}k`;
    return `${sym}${tick}`;
};

// Custom premium tooltip: exact value + Working/Retirement phase at that age.
type CustomTooltipProps = TooltipProps<number, string> & { currency: string };

const CustomTooltip: React.FC<CustomTooltipProps> = ({
    active,
    payload,
    label,
    currency,
}) => {
    if (active && payload && payload.length) {
        const dataPoint = payload[0].payload as WealthTrajectoryPoint;
        const isBankrupt = dataPoint.median_portfolio_value <= 0;

        return (
            <div className="bg-white p-4 border border-slate-200 shadow-xl rounded-lg text-sm z-50">
                <p className="font-bold text-slate-500 mb-1">
                    Age {label}
                    <span
                        className={`ml-1 ${
                            dataPoint.is_retired ? 'text-sky-600' : 'text-emerald-600'
                        }`}
                    >
                        {dataPoint.is_retired ? '(Retired)' : '(Working)'}
                    </span>
                </p>
                <p
                    className={`text-2xl font-extrabold ${
                        isBankrupt ? 'text-red-600' : 'text-slate-900'
                    }`}
                >
                    {formatCurrency(dataPoint.median_portfolio_value, currency)}
                </p>
                {isBankrupt && (
                    <p className="text-red-500 text-xs font-bold mt-1">
                        Portfolio Depleted
                    </p>
                )}
            </div>
        );
    }
    return null;
};

export const TrajectoryChart: React.FC<TrajectoryChartProps> = ({
    data,
    isFetching,
    currency,
}) => {
    // Find the exact age where retirement starts for the visual boundary marker.
    const retirementAge = useMemo(() => {
        const retirementPoint = data.find((p) => p.is_retired);
        return retirementPoint ? retirementPoint.age : null;
    }, [data]);

    const formatYAxis = useMemo(() => makeFormatYAxis(currency), [currency]);

    return (
        <div
            className={`w-full h-full min-h-[350px] transition-opacity duration-300 ${
                isFetching ? 'opacity-50' : 'opacity-100'
            }`}
        >
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                    data={data}
                    margin={{ top: 20, right: 30, left: 10, bottom: 0 }}
                >
                    <defs>
                        {/* A fading gradient under the curve for a premium feel. */}
                        <linearGradient id="ffWealthGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#059669" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#059669" stopOpacity={0.0} />
                        </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />

                    <XAxis
                        dataKey="age"
                        tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(val) => `Age ${val}`}
                        minTickGap={20}
                    />

                    <YAxis
                        tickFormatter={formatYAxis}
                        tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                        tickLine={false}
                        axisLine={false}
                        width={70}
                    />

                    <Tooltip
                        content={<CustomTooltip currency={currency} />}
                        cursor={{
                            stroke: '#94a3b8',
                            strokeWidth: 1,
                            strokeDasharray: '4 4',
                        }}
                    />

                    {retirementAge && (
                        <ReferenceLine
                            x={retirementAge}
                            stroke="#0f172a"
                            strokeDasharray="4 4"
                            label={{
                                position: 'top',
                                value: 'Retirement Begins',
                                fill: '#0f172a',
                                fontSize: 12,
                                fontWeight: 'bold',
                            }}
                        />
                    )}

                    <Area
                        type="monotone"
                        dataKey="median_portfolio_value"
                        stroke="#059669"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#ffWealthGradient)"
                        // Animation off so rapid slider dragging doesn't re-draw
                        // the chart from zero every 400ms (avoids jitter).
                        isAnimationActive={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};
